import clsx from 'clsx';
import { forwardRef, useRef, useState, type ReactNode } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { MOTION } from '@/utils';
import { mergeRefs } from '@/utils/merge-refs.utils';

const ITEMS_THRESHOLD = 5;

export type AdvantageCardProps = {
  title: string;
  tagline: string;
  icon: ReactNode;
  items: (string | ReactNode)[];
  description?: ReactNode;
  footer?: string;
  decoration?: ReactNode;
};

export const AdvantageCard = forwardRef<HTMLDivElement, AdvantageCardProps>(
  ({ title, tagline, icon, items, description, footer }, ref) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const cardRef = useRef<HTMLElement>(null);

    useGSAP(() => {
      const el = cardRef.current;
      if (!el) return;

      const headerEls = gsap.utils.toArray<HTMLElement>('[data-card-header]', el);
      const itemEls   = gsap.utils.toArray<HTMLElement>('[data-card-item]', el);
      const footerEl  = el.querySelector('[data-card-footer]');

      gsap.set(el,        { autoAlpha: 0, y: MOTION.y });
      gsap.set(headerEls, { autoAlpha: 0, y: 8 });
      gsap.set(itemEls,   { autoAlpha: 0, y: 10 });
      if (footerEl) gsap.set(footerEl, { autoAlpha: 0, y: 6 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.to(el,        { autoAlpha: 1, y: 0, duration: MOTION.durationMd, ease: MOTION.ease })
        .to(headerEls, { autoAlpha: 1, y: 0, stagger: 0.07, duration: MOTION.durationSm, ease: MOTION.ease }, '<+0.1')
        .to(itemEls,   { autoAlpha: 1, y: 0, stagger: 0.05, duration: MOTION.durationSm, ease: MOTION.ease }, '<+0.1');

      if (footerEl) {
        tl.to(footerEl, { autoAlpha: 1, y: 0, duration: MOTION.durationSm, ease: MOTION.ease }, '<+0.1');
      }
    }, { scope: cardRef });

    const visibleItems = items.slice(0, ITEMS_THRESHOLD);
    const hiddenItems = items.slice(ITEMS_THRESHOLD);
    const hasMore = hiddenItems.length > 0;

    return (
      <article
        ref={mergeRefs(cardRef, ref)}
        className="border-dark-blue/[0.07] relative flex flex-col justify-between rounded-(--radius-current) border bg-white"
      >
        <div>
          {/* En-tête */}
          <div className="px-7 pt-7 pb-0 md:px-8 md:pt-8">
            <div data-card-header className="mb-4 flex items-center justify-between">
              <span className="font-roboto-medium text-orange inline-flex items-center text-base uppercase">
                {title}
              </span>
              <span className="text-dark-blue/20 mt-0.5">{icon}</span>
            </div>

            <h3 data-card-header className="font-roboto-medium text-midnight-blue mb-2 leading-snug tracking-tight">
              {tagline}
            </h3>

            {description && (
              <p data-card-header className="text-dark-blue/75 mb-6 text-[12.5px] leading-relaxed">{description}</p>
            )}
          </div>

          {/* Séparateur */}
          <div className="bg-dark-blue/7 mx-7 h-px md:mx-8" />

          <div className="relative px-7 md:px-8">
            <div
              className="bg-dark-blue/7 absolute top-0 bottom-0 w-px"
              style={{ left: 'calc(1.75rem + 17px)' }}
            />

            <ul className="relative">
              {visibleItems.map((item, i) => (
                <li
                  key={i}
                  data-card-item
                  className="border-dark-blue/7 flex items-start gap-5 border-b py-4 last:border-b"
                >
                  <div className="border-dark-blue/12 relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-white">
                    <span className="font-roboto-medium text-dark-blue/70 text-xs tabular-nums">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <span className="text-dark-blue pt-[5px] text-base leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>

            {hasMore && (
              <div
                className={clsx(
                  'ease-power2-in-out grid transition-[grid-template-rows] duration-400',
                  isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
              >
                <div className="overflow-hidden">
                  <ul>
                    {hiddenItems.map((item, i) => (
                      <li
                        key={i + ITEMS_THRESHOLD}
                        className="border-dark-blue/7 flex items-start gap-5 border-b py-4 last:border-b"
                      >
                        <div className="border-dark-blue/12 relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-white">
                          <span className="font-roboto-medium text-dark-blue/70 text-xs tabular-nums">
                            {String(i + ITEMS_THRESHOLD + 1).padStart(2, '0')}
                          </span>
                        </div>
                        <span className="text-dark-blue pt-[5px] text-base leading-relaxed">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {hasMore && (
            <button
              className="group border-dark-blue/7 mx-7 flex cursor-pointer items-center justify-between gap-2 py-4 md:mx-8"
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              <span className="font-roboto-medium group-hover:text-orange text-orange text-sm transition-colors duration-200">
                {isExpanded ? 'Voir moins' : 'Voir plus'}
              </span>
            </button>
          )}
        </div>
        {footer && (
          <div data-card-footer className="mx-7 flex items-start gap-2.5 py-4 md:mx-8">
            <span className="relative mt-[0.5em] flex size-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75"></span>
              <span className="bg-orange relative inline-flex size-1.5 rounded-full"></span>
            </span>
            <p className="text-dark-blue text-sm! leading-relaxed">{footer}</p>
          </div>
        )}
      </article>
    );
  },
);

AdvantageCard.displayName = 'AdvantageCard';
